import { useState } from "react";
import { Star } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Per-product favorite (我的收藏) toggle used by ProductsGrid / ProductsTable /
 * MobileProductsList.
 *
 * Props:
 *   - itemNumber: string
 *   - initial: boolean — the ``is_favorite`` flag returned by the list endpoint
 *   - onChange?: (newValue: boolean) => void — parent hook for optimistic list updates
 *
 * Optimistically flips local state on click, then hits
 * ``POST /api/products/{item}/favorite`` (server toggles + returns the new state).
 * Reverts on error and surfaces a toast.
 */
export const FavoriteStar = ({ itemNumber, initial = false, onChange, size = 16, className = "" }) => {
  const { t } = useLanguage();
  const [fav, setFav] = useState(Boolean(initial));
  const [pending, setPending] = useState(false);

  const toggle = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (pending) return;
    const next = !fav;
    setFav(next);       // optimistic
    setPending(true);
    try {
      const { data } = await api.post(`/products/${encodeURIComponent(itemNumber)}/favorite`);
      const actual = Boolean(data?.is_favorite);
      setFav(actual);
      onChange?.(actual);
      // Fire a window event so any ancestor tracking the aggregate count
      // (e.g. the "我的收藏 · N" toolbar badge) can refresh without prop drilling.
      window.dispatchEvent(new CustomEvent("favorites-changed"));
      toast.success(actual ? t("products.addedToFavorites") : t("products.removedFromFavorites"));
    } catch (err) {
      setFav(!next);    // revert
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={fav}
      data-testid={`favorite-toggle-${itemNumber}`}
      className={`inline-flex items-center justify-center rounded-md transition-colors ${
        fav ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground/40 hover:text-amber-500"
      } ${pending ? "opacity-60" : ""} ${className}`}
      title={fav ? t("products.removedFromFavorites") : t("products.addedToFavorites")}
    >
      <Star size={size} className={fav ? "fill-amber-500" : ""} />
    </button>
  );
};
